// Tauri API実装（API抽象化レイヤーを使用）
import { callApi } from './api-client';

// types/index.tsから型をインポート
import { AITreeResponse } from '../types';

// types/api.tsから型をインポート
import type {
  ApiResponse,
  SessionData,
  ChatSession,
  DisplayMessage,
  ModelTagConfig
} from '../types/api';

// ============================================================================
// 型定義（後方互換性のためエクスポート）
// ============================================================================

export interface NodeData {
  word: string;
  prob: number;
}

// 型を再エクスポート（既存コードとの互換性のため）
export type { ApiResponse, SessionData, ChatSession, DisplayMessage, ModelTagConfig };

// 後方互換性のため、DisplayMessageをChatMessageとしてエクスポート
// 実際の使用箇所ではDisplayMessageを使用
// ChatSessionのmessagesはChatMessageInternal（prompt_session_id, timestampのみ）を使用
export type ChatMessage = DisplayMessage;


// ============================================================================
// セッション管理
// ============================================================================

/**
 * 新しいセッションを作成
 */
export const createSession = async (prompt: string): Promise<ApiResponse<SessionData>> => {
  return callApi<SessionData>('create_session', { prompt });
};

/**
 * セッション一覧を取得
 */
export const getSessionList = async (): Promise<ApiResponse<SessionData[]>> => {
  return callApi<SessionData[]>('get_session_list');
};

/**
 * セッションを削除
 */
export const deleteSession = async (sessionId: string): Promise<ApiResponse> => {
  return callApi('delete_session', { sessionId });
};

/**
 * 生成完了状態を確認
 */
export const checkGenerationStatus = async (sessionId: string): Promise<ApiResponse<{ isComplete: boolean; hasContent: boolean; status?: string; needsContinuation?: boolean; currentStep?: number }>> => {
  return callApi<{ isComplete: boolean; hasContent: boolean; status?: string; needsContinuation?: boolean; currentStep?: number }>('check_generation_status', { sessionId });
};

/**
 * AI生成を継続実行
 */
export const continueAIGeneration = async (sessionId: string): Promise<ApiResponse<{ completed: boolean; currentStep: number }>> => {
  return callApi<{ completed: boolean; currentStep: number }>('continue_generation', { sessionId });
};

/**
 * イベント取得
 */
export interface GenerationEvent {
  type: 'word_added' | 'generation_completed';
  sessionId: string;
  timestamp: string;
  data?: {
    nodeId?: string;
    words?: string[];
    success?: boolean;
    error?: string;
  };
}

export const getEvents = async (sessionId: string, since?: number): Promise<ApiResponse<GenerationEvent[]>> => {
  return callApi<GenerationEvent[]>('get_events', { sessionId, since });
};



// ============================================================================
// ツリー操作（シンプル版）
// ============================================================================

/**
 * ツリー構造を取得（セッションIDベース - JSON ファイルから読み込み）
 */
export const getTreeStructure = async (sessionId: string): Promise<ApiResponse<AITreeResponse>> => {
  return callApi<AITreeResponse>('get_tree_structure', { sessionId });
};

/**
 * ノードを追加
 */
export const addNode = async (sessionId: string, parentId: string, word: string): Promise<ApiResponse> => {
  return callApi('add_node', { sessionId, parentId, word });
};

/**
 * ノードを削除
 */
export const deleteNode = async (sessionId: string, nodeId: string): Promise<ApiResponse> => {
  return callApi('delete_node', { sessionId, nodeId });
};

/**
 * ノードを選択
 */
export const selectNode = async (sessionId: string, nodeId: string): Promise<ApiResponse> => {
  return callApi('select_node', { sessionId, nodeId });
};

/**
 * AI生成×10（選択ノードから最確率子ノードを10回連続選択）
 */
export const aiGenerateX10 = async (sessionId: string, nodeId: string): Promise<ApiResponse> => {
  return callApi('ai_generate_x10', { sessionId, nodeId });
};

export const aiGenerateX60 = async (sessionId: string, nodeId: string): Promise<ApiResponse> => {
  return callApi('ai_generate_x60', { sessionId, nodeId });
};

// =============================
// 設定 API（デフォルト確率）
// =============================

export const getDefaultProb = async (): Promise<ApiResponse<number>> => {
  return callApi<number>('get_default_prob');
};

export const setDefaultProb = async (prob: number): Promise<ApiResponse> => {
  return callApi('set_default_prob', { prob });
};

// ============================================================================
// ユーティリティ関数
// ============================================================================

/**
 * エラーハンドリング用のヘルパー関数
 */
export const handleApiError = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'Unknown error occurred';
};

// ============================================================================
// Tauri接続設定
// ============================================================================

export const TAURI_CONFIG = {
  // Tauriコマンド名
  COMMANDS: {
    // セッション管理
    CREATE_SESSION: 'create_session',
    GET_SESSION_LIST: 'get_session_list',
    DELETE_SESSION: 'delete_session',
    
    // ツリー操作
    GET_TREE_STRUCTURE: 'get_tree_structure',
    ADD_NODE: 'add_node',
    DELETE_NODE: 'delete_node',
    SELECT_NODE: 'select_node',
    
    // 対話履歴
    GET_CONVERSATION_HISTORY: 'get_conversation_history',
  },
} as const;

export const TAURI_COMMANDS = TAURI_CONFIG.COMMANDS;

// ============================================================================
// チャット管理 API
// ============================================================================

/**
 * 新しいチャットセッションを作成
 */
export const createChatSession = async (): Promise<ApiResponse<ChatSession>> => {
  return callApi<ChatSession>('create_chat_session');
};

/**
 * チャット一覧を取得
 */
export const getChatList = async (): Promise<ApiResponse<ChatSession[]>> => {
  return callApi<ChatSession[]>('get_chat_list');
};

/**
 * チャットセッションを取得
 */
export const getChatSession = async (chatId: string): Promise<ApiResponse<ChatSession>> => {
  return callApi<ChatSession>('get_chat_session', { chatId });
};

/**
 * チャットを削除（チャットJSONと関連する全セッションファイルを削除）
 */
export const deleteChat = async (chatId: string): Promise<ApiResponse> => {
  console.log('[deleteChat] 削除開始:', chatId);
  const result = await callApi('delete_chat', { chatId });
  console.log('[deleteChat] 削除結果:', result);
  return result;
};

/**
 * チャットセッションから表示用メッセージを取得
 */
export const getChatDisplayMessages = async (chatId: string): Promise<ApiResponse<DisplayMessage[]>> => {
  return callApi<DisplayMessage[]>('get_chat_display_messages', { chatId });
};

/**
 * メッセージを送信
 */
export const sendMessage = async (chatId: string, content: string): Promise<ApiResponse<DisplayMessage>> => {
  return callApi<DisplayMessage>('send_message', { chatId, content });
};

/**
 * AI返答を生成（同期的、完了まで待つ）
 */
export const generateAIResponse = async (chatId: string): Promise<ApiResponse<DisplayMessage>> => {
  return callApi<DisplayMessage>('generate_ai_response', { chatId });
};

/**
 * AI生成を開始（非同期で実行、prompt_session_idを返す）
 */
export const startAIGeneration = async (chatId: string, userMessage: string): Promise<ApiResponse<string>> => {
  return callApi<string>('start_ai_generation', { chatId, userMessage });
};

/**
 * モデルタグ設定を取得
 */
export const getModelTagsConfig = async (modelName?: string): Promise<ApiResponse<ModelTagConfig>> => {
  return callApi<ModelTagConfig>('get_model_tags_config', { modelName: modelName || null });
};

/**
 * モデル名を取得
 */
export const getModelName = async (): Promise<ApiResponse<string | null>> => {
  return callApi<string | null>('get_model_name');
};

/**
 * モデル名を設定
 */
export const setModelName = async (modelName: string | null): Promise<ApiResponse<void>> => {
  const result = await callApi<void>('set_model_name', { modelName });
  // 設定変更時にタグ設定のキャッシュをクリア
  if (result.success) {
    const { clearTagsCache } = await import('../utils/extractLastUserMessage');
    clearTagsCache();
  }
  return result;
};

/**
 * 利用可能なモデル一覧を取得
 */
export const getModelList = async (): Promise<ApiResponse<string[]>> => {
  console.log('[getModelList] モデル一覧を取得します');
  const result = await callApi<string[]>('get_model_list');
  console.log('[getModelList] 取得結果:', result);
  return result;
};
